import React from "react";
import { ArrowUpRight, Wallet } from "lucide-react";
import { useLang } from "../i18n/LangContext";

// 14 countries (Europe + AU + CA + others) with images & tuition
const countries = [
  { code: "uk",  flag: "🇬🇧", tuition: "£12,000+",  image: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=900" },
  { code: "usa", flag: "🇺🇸", tuition: "$15,000+",  image: "https://images.pexels.com/photos/6147166/pexels-photo-6147166.jpeg?w=900" },
  { code: "nl",  flag: "🇳🇱", tuition: "€11,000+",  image: "https://images.unsplash.com/photo-1584003564911-a7a321c84e1c?w=900" },
  { code: "ch",  flag: "🇨🇭", tuition: "CHF 25,000+", image: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=900" },
  { code: "de",  flag: "🇩🇪", tuition: "€500+",     image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=900" },
  { code: "fr",  flag: "🇫🇷", tuition: "€3,000+",   image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=900" },
  { code: "es",  flag: "🇪🇸", tuition: "€2,500+",   image: "https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=900" },
  { code: "it",  flag: "🇮🇹", tuition: "€3,000+",   image: "https://images.unsplash.com/photo-1525874684015-58379d421a52?w=900" },
  { code: "au",  flag: "🇦🇺", tuition: "AUD 22,000+", image: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=900" },
  { code: "ca",  flag: "🇨🇦", tuition: "CAD 20,000+", image: "https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=900" },
  { code: "ie",  flag: "🇮🇪", tuition: "€10,000+",  image: "https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=900" },
  { code: "se",  flag: "🇸🇪", tuition: "€10,000+",  image: "https://images.unsplash.com/photo-1509356843151-3e7d96241e11?w=900" },
  { code: "at",  flag: "🇦🇹", tuition: "€1,500+",   image: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=900" },
  { code: "pl",  flag: "🇵🇱", tuition: "€2,000+",   image: "https://images.unsplash.com/photo-1607427293702-036933bbf746?w=900" }
];

const Countries = ({ onOpenForm }) => {
  const { t } = useLang();
  // Duplicate list for seamless loop
  const loop = [...countries, ...countries];
  return (
    <section id="countries" className="relative py-20 md:py-28 section-soft overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-3">{t("countries.kicker")}</div>
            <h2 className="headline-huge text-3xl md:text-5xl max-w-2xl text-balance">{t("countries.title")}</h2>
          </div>
          <p className="text-slate-600 max-w-md">{t("countries.subtitle")}</p>
        </div>
      </div>

      {/* Auto-scrolling marquee */}
      <div className="relative overflow-hidden scroll-fade-mask group">
        <div className="marquee-countries gap-5 md:gap-6 py-2">
          {loop.map((c, i) => {
            const name = t(`countryNames.${c.code}`, c.code.toUpperCase());
            return (
              <button
                key={`${c.code}-${i}`}
                onClick={onOpenForm}
                className="shrink-0 w-[280px] md:w-[320px] aspect-[4/5] rounded-3xl overflow-hidden relative card-hover text-left"
                style={{ border: '1px solid rgba(27,42,74,0.08)' }}
              >
                <img src={c.image} alt={name} className="absolute inset-0 w-full h-full object-cover group-hover/card:scale-110 transition duration-700" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(27,42,74,0.85) 0%, rgba(27,42,74,0.25) 45%, transparent 70%)' }} />

                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-xs font-semibold" style={{ color: '#1B2A4A' }}>
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="uppercase tracking-wider">{c.code === 'uk' || c.code === 'usa' ? c.code.toUpperCase() : c.code.toUpperCase()}</span>
                </div>

                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="font-display text-2xl md:text-3xl uppercase leading-tight">{name}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Wallet className="w-3.5 h-3.5" style={{ color: '#DBC097' }} />
                      <span className="text-xs font-semibold" style={{ color: '#E8D5AB' }}>{c.tuition}</span>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center transition group-hover:bg-amber-500" style={{ color: '#1B2A4A' }}>
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Countries;
